
import React from 'react';
import { EditMode, QuickEditAction, DisplayedImage } from '../types';
import LoadingSpinner from './LoadingSpinner';
import CustomActionDesigner from './CustomActionDesigner';
import QuickEditPanel from './QuickEditPanel'; 
import { formatImageSuffix } from '../App'; 

interface EditPanelProps {
  editMode: EditMode;
  onSetEditMode: (mode: EditMode) => void;
  generationPrompt: string;
  onGenerationPromptChange: (prompt: string) => void;
  onGenerate: () => void;
  analysisPrompt: string;
  onAnalysisPromptChange: (prompt: string) => void;
  onAnalyze: () => void;
  analysisResult: string | null;
  isProcessing: boolean;
  selectedImageName: string | null; 
  selectedImageParsedDate?: string;
  selectedImageParsedTime?: string;
  selectedImageParsedMachineCode?: string;
  selectedImageDescription: string | null; 
  currentImage: DisplayedImage | null; 
  quickEditActions: QuickEditAction[];
  onApplyQuickEdit: (action: QuickEditAction) => void;
  customQuickEditActions: QuickEditAction[];
  onAddCustomQuickEdit: (action: Omit<QuickEditAction, 'id' | 'promptGenerator' | 'isCustom'> & { promptTemplate: string }) => void;
  onDebugHover?: (name: string | null) => void; // New prop for debug hover
}

const TabButton: React.FC<{active: boolean; onClick: () => void; children: React.ReactNode; 'aria-controls'?: string; 'id'?: string; onMouseEnter?: () => void; onMouseLeave?: () => void;}> = ({ active, onClick, children, onMouseEnter, onMouseLeave, ...props }) => (
  <button
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    role="tab"
    aria-selected={active}
    className={`px-3 py-2 sm:px-4 text-xs sm:text-sm font-medium transition-colors duration-150 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400
      ${active ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-gray-100'}`}
    {...props}
  >
    {children}
  </button>
);

const EditPanel: React.FC<EditPanelProps> = ({
  editMode,
  onSetEditMode,
  generationPrompt,
  onGenerationPromptChange,
  onGenerate,
  analysisPrompt,
  onAnalysisPromptChange,
  onAnalyze,
  analysisResult,
  isProcessing,
  selectedImageName,
  selectedImageParsedDate,
  selectedImageParsedTime,
  selectedImageParsedMachineCode,
  selectedImageDescription,
  currentImage,
  quickEditActions,
  onApplyQuickEdit,
  customQuickEditActions,
  onAddCustomQuickEdit,
  onDebugHover,
}) => {

  const commonTextAreaClasses = "w-full p-2 rounded-md bg-gray-700 border border-gray-600 focus:ring-indigo-500 focus:border-indigo-500 text-gray-100 resize-none text-sm";
  const commonButtonClasses = "w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-md transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm";
  
  const getSelectedImageDisplayNameWithSuffix = () => {
    if (!selectedImageName) return null;
    const suffix = formatImageSuffix(selectedImageParsedDate, selectedImageParsedTime, selectedImageParsedMachineCode) || "";
    return `${selectedImageName}${suffix}`;
  };
  
  const getCurrentImageDisplayNameWithSuffix = () => {
    if (!currentImage?.displayName && !currentImage?.alt) return "imagen seleccionada";
    const nameBase = currentImage?.displayName || currentImage?.alt || "";
    const suffix = formatImageSuffix(currentImage?.parsedDate, currentImage?.parsedTime, currentImage?.parsedMachineCode) || "";
    const namePart = nameBase.length > 40 ? nameBase.substring(0,37) + "..." : nameBase;
    return `${namePart}${suffix}`;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-4 border-b border-gray-700">
        <nav className="flex space-x-1 sm:space-x-2" aria-label="Edit Modes">
          <TabButton 
            active={editMode === EditMode.QuickEdit} 
            onClick={() => onSetEditMode(EditMode.QuickEdit)} 
            onMouseEnter={() => onDebugHover?.("EditPanel Tab: Ediciones Rápidas")}
            onMouseLeave={() => onDebugHover?.(null)}
            id="tab-quickedit" aria-controls="panel-quickedit">Ediciones Rápidas</TabButton>
          <TabButton 
            active={editMode === EditMode.Generate} 
            onClick={() => onSetEditMode(EditMode.Generate)} 
            onMouseEnter={() => onDebugHover?.("EditPanel Tab: Generar Nueva")}
            onMouseLeave={() => onDebugHover?.(null)}
            id="tab-generate" aria-controls="panel-generate">Generar Nueva</TabButton>
          <TabButton 
            active={editMode === EditMode.Analyze} 
            onClick={() => onSetEditMode(EditMode.Analyze)} 
            onMouseEnter={() => onDebugHover?.("EditPanel Tab: Analizar Imagen")}
            onMouseLeave={() => onDebugHover?.(null)}
            id="tab-analyze" aria-controls="panel-analyze">Analizar Imagen</TabButton>
          <TabButton 
            active={editMode === EditMode.ManageCustomEdits} 
            onClick={() => onSetEditMode(EditMode.ManageCustomEdits)} 
            onMouseEnter={() => onDebugHover?.("EditPanel Tab: Gestionar Edits")}
            onMouseLeave={() => onDebugHover?.(null)}
            id="tab-manageedits" aria-controls="panel-manageedits">Gestionar Edits Personalizados</TabButton>
        </nav>
      </div>

      <div className="flex-grow overflow-y-auto pr-1 sm:pr-2 space-y-4 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
        {editMode === EditMode.QuickEdit && (
          <div role="tabpanel" id="panel-quickedit" aria-labelledby="tab-quickedit">
            <QuickEditPanel
              quickEditActions={quickEditActions}
              onApplyQuickEdit={onApplyQuickEdit}
              isProcessing={isProcessing}
              selectedImageName={selectedImageName}
              selectedImageParsedDate={selectedImageParsedDate}
              selectedImageParsedTime={selectedImageParsedTime}
              selectedImageParsedMachineCode={selectedImageParsedMachineCode}
              selectedImageDescription={selectedImageDescription}
              onDebugHover={onDebugHover}
            />
          </div>
        )}

        {editMode === EditMode.Generate && (
          <div role="tabpanel" id="panel-generate" aria-labelledby="tab-generate">
            <div>
              <label htmlFor="generationPrompt" className="block text-sm font-medium text-gray-300 mb-1">
                Describe la nueva imagen que quieres generar:
              </label>
              <textarea
                id="generationPrompt"
                rows={3}
                className={commonTextAreaClasses}
                value={generationPrompt}
                onChange={(e) => onGenerationPromptChange(e.target.value)}
                placeholder="Ej: Una ciudad futurista al atardecer, hiperrealista..."
                disabled={isProcessing}
                onMouseEnter={() => onDebugHover?.("EditPanel Input: Generation Prompt")}
                onMouseLeave={() => onDebugHover?.(null)}
              />
              {getSelectedImageDisplayNameWithSuffix() && (
                <p className="text-xs text-gray-400 mt-1">
                  Consejo: Puedes basar tu prompt en "{getSelectedImageDisplayNameWithSuffix()}" o su descripción para crear variaciones.
                </p>
              )}
            </div>
            <button 
              onClick={onGenerate} 
              disabled={isProcessing || !generationPrompt} 
              className={commonButtonClasses + " mt-3"}
              onMouseEnter={() => onDebugHover?.("EditPanel Button: Generate")}
              onMouseLeave={() => onDebugHover?.(null)}
            >
              {isProcessing ? <LoadingSpinner size="sm" /> : 'Generar con Gemini'}
            </button>
          </div>
        )}

        {editMode === EditMode.Analyze && (
           <div role="tabpanel" id="panel-analyze" aria-labelledby="tab-analyze">
            {!currentImage && (
              <p className="text-gray-400 text-center py-4">Selecciona o genera una imagen para analizar.</p>
            )}
            {currentImage && (
              <>
                <div>
                  <label htmlFor="analysisPrompt" className="block text-sm font-medium text-gray-300 mb-1">
                    Pregunta algo sobre <strong className="text-indigo-300">{getCurrentImageDisplayNameWithSuffix()}</strong>:
                  </label>
                  <textarea
                    id="analysisPrompt"
                    rows={3}
                    className={commonTextAreaClasses}
                    value={analysisPrompt}
                    onChange={(e) => onAnalysisPromptChange(e.target.value)}
                    placeholder="Ej: ¿Cuál es el sujeto principal? ¿Hay animales?"
                    disabled={isProcessing}
                    onMouseEnter={() => onDebugHover?.("EditPanel Input: Analysis Prompt")}
                    onMouseLeave={() => onDebugHover?.(null)}
                  />
                </div>
                <button 
                  onClick={onAnalyze} 
                  disabled={isProcessing || !analysisPrompt || !currentImage} 
                  className={commonButtonClasses + " mt-3"}
                  onMouseEnter={() => onDebugHover?.("EditPanel Button: Analyze")}
                  onMouseLeave={() => onDebugHover?.(null)}
                >
                  {isProcessing ? <LoadingSpinner size="sm" /> : 'Analizar con Gemini'}
                </button>
                {analysisResult && !isProcessing && (
                  <div className="mt-3 p-3 bg-gray-700 rounded-md">
                    <h4 className="font-semibold text-gray-200 mb-1">Resultado del Análisis:</h4>
                    <p className="text-sm text-gray-300 whitespace-pre-wrap">{analysisResult}</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        
        {editMode === EditMode.ManageCustomEdits && (
           <div role="tabpanel" id="panel-manageedits" aria-labelledby="tab-manageedits">
            <CustomActionDesigner
              onAddCustomQuickEdit={onAddCustomQuickEdit}
              existingCustomActions={customQuickEditActions}
            />
           </div>
        )}
      </div>
    </div>
  );
};

export default EditPanel;
